import type { Player, EventMap, TimeEvent, EventCallback } from '@vimeo/player';
import deepmerge from 'deepmerge';
import { v4 as uuid } from 'uuid';

export type VimeoPlayerEventHandlers = {
  //======================================================================
  // Playback events
  // https://segment.com/docs/connections/spec/video/#playback
  //======================================================================

  /**
   * When a user presses Play.
   */
  onPlaybackStarted?: (metadata: VimeoPlayerAnalyticsMetadata) => void;

  /**
   * When user presses Pause.
   */
  onPlaybackPaused?: (metadata: VimeoPlayerAnalyticsMetadata) => void;

  /**
   * When playback is resumed, by the user, after being paused.
   */
  onPlaybackResumed?: (metadata: VimeoPlayerAnalyticsMetadata) => void;

  /**
   * When a user manually seeks a certain position of the content in the playback.
   * `seekPosition` denotes where the user is seeking to, and `position` denotes
   * where the user is seeking from.
   */
  onPlaybackSeek?: (metadata: VimeoPlayerAnalyticsMetadata, seekPosition: number) => void;

  /**
   * When playback is complete.
   */
  onPlaybackCompleted?: (metadata: VimeoPlayerAnalyticsMetadata) => void;

  //======================================================================
  // Content events
  // https://segment.com/docs/connections/spec/video/#content
  //======================================================================

  /**
   * When a video content segment starts playing within a playback.
   */
  onContentStarted?: (metadata: VimeoPlayerAnalyticsMetadata) => void;

  /**
   * Heartbeats that you can fire every n seconds to track how far
   * into the content the user is currently viewing as indicated by the `position`.
   */
  onContentPlaying?: (metadata: VimeoPlayerAnalyticsMetadata) => void;

  /**
   * When a video content segment completes playing within a playback.
   * That is, position and totalLength are equal.
   */
  onContentCompleted?: (metadata: VimeoPlayerAnalyticsMetadata) => void;
};

export type VimeoPlayerAnalyticsMetadata = {
  playbackSessionId: string;
  videoId?: number | undefined;
  content: {
    title?: string;
    description?: string;
  };
  playback: {
    fullScreen?: boolean;
    sound?: number;
    /**
     * Current index position in seconds of the playhead
     */
    position?: number;

    /**
     * Total duration of the playback in seconds.
     */
    totalLength?: number;
  };
};

type VimeoEventMap = {
  [EventName in keyof EventMap]?: EventCallback<EventMap[EventName]>;
};

type VimeoPlayerAnalyticsOptions = {
  /**
   * Rate in seconds in which we track heartbeat events
   *
   * @default 10
   */
  heartbeatInterval?: number;
};

/**
 * This module partially implements the segment video spec.
 * https://segment.com/docs/connections/spec/video
 *
 *
 * TODO:
 * - `seeking` event doesn't report position before actual seek occurs.
 *   Will need to figure out how to track this.
 *   https://github.com/vimeo/player.js/issues/705
 */
export class VimeoPlayerAnalytics {
  private player: Player;
  private metadata: VimeoPlayerAnalyticsMetadata;

  /**
   * Most recent index position in seconds of the playhead as calculated from the interval.
   */
  private mostRecentHeartbeat: number;

  /**
   * Helps differentiate between starting and resuming a video.
   */
  private isPaused: boolean = false;

  private _isPlaying: boolean;

  /**
   * Keep track of seeking events. Required to properly set most heartbeat tracker after seek.
   */
  private isSeeking: boolean = false;

  private eventCallbacks: VimeoPlayerEventHandlers;
  private heartbeatInterval: number;

  constructor(player: Player, eventCallbacks?: VimeoPlayerEventHandlers, options: VimeoPlayerAnalyticsOptions = {}) {
    this.player = player;
    this.metadata = {
      playbackSessionId: uuid(),
      content: {},
      playback: {},
    };

    this.mostRecentHeartbeat = 0;
    this.heartbeatInterval = options.heartbeatInterval ?? 10;
    this.setEventCallbacks(eventCallbacks);
  }

  public initialize() {
    const player = this.player;

    const eventHandlers = this.getPlayerEventHandlers();

    for (const evt in eventHandlers) {
      // type (evtData: EventMap[Evt]) => void is not assignable to type EventCallback<EventMap[Evt]>
      // @ts-ignore
      this.registerHandler(evt as keyof VimeoEventMap, eventHandlers[evt]);
    }

    player
      .getVideoId()
      .then(id => {
        this.retrieveMetadata({ id });
      })
      .catch(console.error);
  }

  public getMetadata() {
    return this.metadata;
  }

  public get isPlaying() {
    return this._isPlaying;
  }

  public stop() {
    const player = this.player;
    const eventHandlers = this.getPlayerEventHandlers();

    Object.keys(eventHandlers).forEach(eventName => {
      player.off(eventName);
    });
  }

  /**
   * This allows you to update the event handlers without having to create a new analytics instance.
   *
   * @param next Event handlers to be used when the video player fires a corresponding event.
   */
  public setEventCallbacks(next: VimeoPlayerEventHandlers = {}) {
    this.eventCallbacks = next || {};
  }

  //

  private getPlayerEventHandlers(): VimeoEventMap {
    return {
      loaded: this.retrieveMetadata,
      fullscreenchange: this.handleFullscreenChange,
      volumechange: this.handleVolumeChange,
      play: this.trackPlay,
      pause: this.trackPause,
      ended: this.trackEnded,
      seeking: this.trackSeeking,
      seeked: this.trackSeeked,
      timeupdate: this.trackHeartbeat,
    };
  }

  private registerHandler<Evt extends keyof VimeoEventMap>(e: Evt, handler: (evtData: EventMap[Evt]) => void) {
    this.player.on<Evt>(e, evtData => {
      if (typeof (evtData as TimeEvent)?.seconds === 'number') {
        this.updatePlaybackPosition(evtData as TimeEvent);
      }
      handler.call(this, evtData);
    });
  }

  private retrieveMetadata({ id }: { id: number }) {
    const player = this.player;
    this.metadata.videoId = id;
    Promise.all([player.getVideoTitle(), player.getDuration(), player.getVolume(), player.getFullscreen()])
      .then(([videoTitle, videoDuration, volume, fullScreen]) => {
        this.metadata.content.title = videoTitle;

        this.metadata.playback.position = 0;
        this.metadata.playback.sound = 100 * volume;
        this.metadata.playback.fullScreen = fullScreen;
        this.metadata.playback.totalLength = videoDuration;
      })
      .catch(errors => {
        console.error(`Unable to retrieve video metadata`, errors);
      });
  } //

  private handleFullscreenChange(evt: EventMap['fullscreenchange']) {
    this.metadata.playback.fullScreen = evt.fullscreen;
  }

  private handleVolumeChange(evt: EventMap['volumechange']) {
    this.metadata.playback.sound = 100 * evt.volume;
  }

  private updatePlaybackPosition(evt: TimeEvent) {
    this.metadata.playback.position = Math.floor(evt.seconds);
  }

  private trackPlay(evt: TimeEvent) {
    if (!evt) {
      return;
    }
    if (this.isPaused) {
      // Reset `isPaused` state
      this.isPaused = false;
      this.logEvent('trackResumed', evt);
      this.eventCallbacks.onPlaybackResumed?.(this.prepareMetadataForSend());
    } else {
      this.logEvent('trackStarted', evt);
      this.eventCallbacks.onPlaybackStarted?.(this.prepareMetadataForSend());
      this.eventCallbacks.onContentStarted?.(this.prepareMetadataForSend());
    }

    this._isPlaying = true;
  }

  private trackPause(evt: TimeEvent) {
    this.isPaused = true;
    this._isPlaying = false;

    // Vimeo player fires a `pause` event when the video is completed.
    // `evt.percent` is how far progressed the video is.
    if (evt.percent !== 1) {
      this.logEvent('trackPause', evt);
      this.eventCallbacks.onPlaybackPaused?.(this.prepareMetadataForSend());
    }
  }

  private trackEnded(evt: TimeEvent) {
    this._isPlaying = false;
    this.logEvent('trackEnded', evt);
    this.eventCallbacks.onPlaybackCompleted?.(this.prepareMetadataForSend());
    this.eventCallbacks.onContentCompleted?.(this.prepareMetadataForSend());
  }

  private trackHeartbeat(evt: EventMap['timeupdate']) {
    this.metadata.playback.position = Math.floor(evt.seconds);
    const latestHeartbeat = this.mostRecentHeartbeat;
    const currentPosition = this.metadata.playback.position;

    if (
      typeof currentPosition === 'number' &&
      latestHeartbeat !== currentPosition &&
      // 10~ seconds have elapsed since last heartbeat
      (currentPosition - latestHeartbeat >= this.heartbeatInterval || this.isSeeking)
    ) {
      this.mostRecentHeartbeat = Math.floor(currentPosition);
      this.logEvent('trackTimeupdate', evt);
      this.eventCallbacks.onContentPlaying?.(this.metadata);
    }
  }
  private trackSeeking(evt: TimeEvent) {
    this.isSeeking = true;
    this.logEvent('trackSeeking', evt);
  }
  private trackSeeked(evt: TimeEvent) {
    this.isSeeking = false;
    this.logEvent('trackSeeked', evt);
  }

  private prepareMetadataForSend() {
    return deepmerge({}, this.metadata);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private logEvent(evtName: string, evt: any, extraArgs: {} = {}) {
    // console.log(`>>> ${evtName}`, {
    //   pos: this.metadata.playback.position,
    //   evt,
    //   isPaused: this.isPaused,
    //   metadata: deepmerge({}, this.metadata),
    //   heartbeat: this.mostRecentHeartbeat,
    //   ...extraArgs
    // });
  }
}
